import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@contexts/AuthContext';
import { CircularProgress, Typography } from '@mui/joy';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredRole?: string;
  requiredPermission?: string;
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  requiredRole,
  requiredPermission,
}) => {
  const { isAuthenticated, isLoading, hasRole, hasPermission } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <CircularProgress size="lg" />
          <Typography level="body-md" className="mt-4">
            Loading...
          </Typography>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    // Redirect to login page but save the attempted location
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (requiredRole && !hasRole(requiredRole)) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <Typography level="h3" className="text-red-600 mb-2">
            Access Denied
          </Typography>
          <Typography level="body-md" className="text-gray-600">
            You don&apos;t have the required role to access this page.
          </Typography>
        </div>
      </div>
    );
  }

  if (requiredPermission && !hasPermission(requiredPermission)) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <Typography level="h3" className="text-red-600 mb-2">
            Access Denied
          </Typography>
          <Typography level="body-md" className="text-gray-600">
            You don&apos;t have the required permission to access this page.
          </Typography>
        </div>
      </div>
    );
  }

  return <>{children}</>;
};